import { useForm } from 'react-hook-form';
import { useAppSelector } from 'store/index';
import { selectCompany, selectCompanyId } from 'store/selectors/me/company';
import {
  SettingsDescription,
  SettingsMain,
  SettingsPageHeader,
  SettingsSectionTitle,
} from '../SettingsLayout';
import CommerceTypeAvatar from 'storybookConfig/stories/cells/Avatar/CommerceTypeAvatar';
import { CommerceType } from 'types/models/company';
import Form from 'storybookConfig/stories/organisms/Form';
import Input from 'storybookConfig/stories/molecules/Input';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import useAlertQueue from 'hooks/useAlertQueue';
import type { Company, CompanyMarketplace } from 'types/models/company';
import { URL_FIELD_REGEX, normalizeURL } from 'utils/strings';

const socials = [
  {
    key: 'websiteURL',
    label: 'Website',
  },
  {
    key: 'instagramURL',
    label: 'Instagram',
  },
  {
    key: 'facebookURL',
    label: 'Facebook',
  },
  {
    key: 'twitterURL',
    label: 'Twitter',
  },
  {
    key: 'linkedInURL',
    label: 'LinkedIn',
  },
  {
    key: 'tikTokURL',
    label: 'TikTok',
  },
];

// URL validation function using the existing regex for consistency
const validateURL = (value: string) => {
  if (!value || value.trim() === '') return true; // Optional field

  const trimmedUrl = value.trim();

  // Remove protocol for regex validation if present
  const urlWithoutProtocol = trimmedUrl.replace(/^https?:\/\//, '');

  if (!URL_FIELD_REGEX.test(urlWithoutProtocol)) {
    return 'Please enter a valid URL';
  }

  return true;
};

type FormFields = Pick<
  CompanyMarketplace,
  'websiteURL' | 'instagramURL' | 'facebookURL' | 'twitterURL' | 'linkedInURL' | 'tikTokURL'
>;

const SocialLinks = () => {
  const company = useAppSelector(selectCompany);
  const companyId = useAppSelector(selectCompanyId);
  const { commerceType } = company ?? {};
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();

  const { handleSubmit, register, formState } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      websiteURL: company?.marketplaceSettings?.websiteURL,
      instagramURL: company?.marketplaceSettings?.instagramURL,
      facebookURL: company?.marketplaceSettings?.facebookURL,
      twitterURL: company?.marketplaceSettings?.twitterURL,
      linkedInURL: company?.marketplaceSettings?.linkedInURL,
      tikTokURL: company?.marketplaceSettings?.tikTokURL,
    },
  });

  const { mutate: updateSocialLinks, isLoading } = useMutation<Company, Error, FormFields>({
    mutationFn: (data: FormFields) => {
      // Normalize URLs before saving
      const normalizedData = {
        websiteURL: data.websiteURL ? normalizeURL(data.websiteURL) : data.websiteURL,
        instagramURL: data.instagramURL ? normalizeURL(data.instagramURL) : data.instagramURL,
        facebookURL: data.facebookURL ? normalizeURL(data.facebookURL) : data.facebookURL,
        twitterURL: data.twitterURL ? normalizeURL(data.twitterURL) : data.twitterURL,
        linkedInURL: data.linkedInURL ? normalizeURL(data.linkedInURL) : data.linkedInURL,
        tikTokURL: data.tikTokURL ? normalizeURL(data.tikTokURL) : data.tikTokURL,
      };

      return updateCompany(companyId ?? '', {
        marketplaceSettings: normalizedData,
      });
    },
    onSuccess: () => {
      addSuccessAlert('Social Links Updated', 'Your social links have been saved successfully.');
    },
    onError: (error) => {
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update social links', error);
    },
  });

  const onSubmit = (data: FormFields) => {
    updateSocialLinks(data);
  };

  return (
    <SettingsMain>
      <SettingsPageHeader title="Social Links">
        <CommerceTypeAvatar commerceType={commerceType || CommerceType.None} />
      </SettingsPageHeader>
      <SettingsSectionTitle>Social Media Profiles</SettingsSectionTitle>
      <SettingsDescription>
        Add your social media profiles to help retailers discover and connect with your brand.
      </SettingsDescription>
      <Form onSubmit={handleSubmit(onSubmit)} style={{ gap: '16px' }}>
        <Form.FieldGroup>
          {socials.map((social) => (
            <Form.Field
              key={social.key}
              labelText={social.label}
              id={social.key}
              errorText={formState.errors[social.key as keyof FormFields]?.message}
            >
              <Input
                {...register(social.key as keyof FormFields, {
                  validate: validateURL,
                })}
                placeholder={`https://www.${social.label.toLowerCase()}.com/yourbrand`}
                isFullWidth
              />
            </Form.Field>
          ))}
        </Form.FieldGroup>
        <Form.Footer justifyEnd>
          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </Form.Footer>
      </Form>
    </SettingsMain>
  );
};

export default SocialLinks;
